/* Alba Duarte — core UI JS */
(() => {
  'use strict';

  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const dispatch = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  const normalizeToken = (value = '') => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[_+./-]+/g, ' ').replace(/\s+/g, ' ').trim();
  const colorAliases = {
    blanco: ['blanco', 'white', 'marfil', 'ivory', 'wit'],
    negro: ['negro', 'black', 'zwart'],
    beige: ['beige', 'crema', 'cream', 'arena', 'sand', 'nude'],
    rojo: ['rojo', 'red', 'burdeos', 'burgundy', 'granate', 'vino', 'wine', 'rood'],
    azul: ['azul', 'blue', 'celeste', 'navy', 'marino', 'blauw'],
    verde: ['verde', 'green', 'pistacho', 'pistachio', 'sage', 'salvia', 'oliva', 'olive', 'esmeralda', 'emerald', 'groen'],
    rosa: ['rosa', 'pink', 'fucsia', 'fuchsia', 'roze'],
    amarillo: ['amarillo', 'yellow', 'lemon', 'limon', 'mostaza', 'mustard', 'geel'],
    naranja: ['naranja', 'orange', 'oranje'],
    gris: ['gris', 'gray', 'grey', 'grijs'],
    morado: ['morado', 'purple', 'lila', 'lilac', 'lavanda', 'lavender', 'paars'],
    marron: ['marron', 'brown', 'chocolate', 'camel', 'bruin'],
      cafe: ['cafe', 'coffee'],
  };
  function colorNeedles(value) {
    const normalized = normalizeToken(value);
    if (!normalized) return [];
    const exact = [normalized];
    for (const aliases of Object.values(colorAliases)) {
      if (aliases.some(alias => normalized === alias || normalized.includes(alias))) return [...new Set([...exact, ...aliases])];
    }
    return exact;
  }
  function mediaMatchesColor(token, color) {
    const haystack = ` ${normalizeToken(token)} `;
    if (!haystack.trim() || !color) return false;
    const exact = normalizeToken(color);
    if (exact && haystack.includes(` ${exact} `)) return true;
    if (!exact.includes(' y ') && (
      haystack.includes(' blanco y negro ') || haystack.includes(' negro y blanco ') ||
      haystack.includes(' black and white ') || haystack.includes(' white and black ')
    )) return false;
    const compactSupplierTokens = new Set(['wit', 'zwart', 'rood', 'blauw', 'groen', 'roze', 'geel', 'paars', 'bruin', 'oranje', 'grijs']);
    return colorNeedles(color).some(needle => {
      const normalizedNeedle = normalizeToken(needle);
    if (!normalizedNeedle) return false;
        if (haystack.includes(` ${normalizedNeedle} `)) return true;
        if (compactSupplierTokens.has(normalizedNeedle) && haystack.includes(normalizedNeedle)) return true;
        // Filenames often fuse the color with another word (e.g. "brownmodelo", "cafemodelo",
        // "detallescafe"), which breaks the whole-word match above. Fall back to a plain
        // substring check for longer color words so these still get grouped correctly.
        return normalizedNeedle.length >= 4 && haystack.includes(normalizedNeedle);
    });
  }
  function itemMatchesFeatured(item, featuredMedia) {
    if (!featuredMedia) return false;
    if (featuredMedia.id && String(item.dataset.mediaId) === String(featuredMedia.id)) return true;
    const srcToken = normalizeToken(String(featuredMedia.src || '').split('?')[0].split('/').pop() || '');
    return !!srcToken && normalizeToken(item.dataset.mediaToken).includes(srcToken);
  }
  const mediaRangeOverrides = {
    'miriam-vestido-maxi-con-hombros-descubiertos-y-alto-abertura': { rosa: [0, 4], negro: [4, 8], marino: [8, 12], rojo: [12, 16], blanco: [16, 19] },
    'marla-vestido-maxi-con-encaje-y-mangas-largas': { rosa: [0, 4], blanco: [4, 6], rojo: [6, 9], negro: [9, 11] },
    'isa-vestido-con-detalles-de-pliegues': { verde: [0, 6], negro: [6, 9], blanco: [9, 11] },
    'avelora-vestido-maxi': { amarillo: [0, 5], 'verde oscuro': [5, 10], verde: [5, 10], oliva: [10, 15], rojo: [15, 19], burdeos: [15, 19] },
    'michele-vestido-mini-bandage-con-escote-redondo': { rojo: [0, 4], rosa: [4, 8], negro: [8, 13], dorado: [13, 18] },
    'glenda-vestido-midi-con-escote-halter-y-falda-acampanada': { rosa: [0, 4], marino: [4, 9], azul: [4, 9], morado: [9, 12], beige: [12, 16] },
  };
  function manualMediaCandidates(items, color) {
    const handle = decodeURIComponent(location.pathname.split('/products/')[1] || '').split(/[/?#]/)[0];
    const productOverrides = mediaRangeOverrides[handle];
    if (!productOverrides) return [];
    const normalizedColor = normalizeToken(color);
    const range = productOverrides[normalizedColor] || Object.entries(productOverrides).find(([key]) => normalizedColor.includes(key))?.[1];
    return range ? items.slice(range[0], Math.min(range[1], items.length)) : [];
  }
  function mediaCandidates(items, color, context = {}) {
    const manual = manualMediaCandidates(items, color);
    if (manual.length) return manual;
    const colors = context.colors || [];
    const featuredByColor = context.featuredByColor || {};
    const explicitFor = value => {
      const featured = featuredByColor[normalizeToken(value)];
      return items.filter(item => mediaMatchesColor(item.dataset.mediaToken, value) || itemMatchesFeatured(item, featured));
    };
    const explicit = explicitFor(color);
    // Product galleries with theme-owned, colour-labelled media are
    // authoritative. Even a colour with a single supplied composite image
    // must not inherit neighbouring colours through the generic inference.
    if (items.some(item => item.dataset.variantMedia === 'true')) return explicit;
    if (explicit.length >= 2 || colors.length < 2) return explicit;

    const targetIndex = colors.findIndex(value => normalizeToken(value) === normalizeToken(color));
    if (targetIndex < 0) return explicit;
    const groups = colors.map(value => explicitFor(value));
    if (explicit.length === 0 && groups.every(group => group.length === 0) && items.length >= colors.length) {
      const start = Math.floor((targetIndex * items.length) / colors.length);
      const end = Math.floor(((targetIndex + 1) * items.length) / colors.length);
      return items.slice(start, Math.max(start + 1, end));
    }
    let previousColor = -1;
    let nextColor = colors.length;
    for (let index = targetIndex - 1; index >= 0; index -= 1) {
      if (groups[index].length) { previousColor = index; break; }
    }
    for (let index = targetIndex + 1; index < colors.length; index += 1) {
      if (groups[index].length) { nextColor = index; break; }
    }
    const unresolved = colors.slice(previousColor + 1, nextColor).filter((_, offset) => groups[previousColor + 1 + offset].length === 0);
    if (explicit.length === 0 && (unresolved.length !== 1 || normalizeToken(unresolved[0]) !== normalizeToken(color))) return explicit;
    if (explicit.length === 1 && unresolved.length > 0) return explicit;

    const itemIndex = item => items.indexOf(item);
    const previousEnd = previousColor >= 0 ? Math.max(...groups[previousColor].map(itemIndex)) : -1;
    const nextStart = nextColor < colors.length ? Math.min(...groups[nextColor].map(itemIndex)) : items.length;
    const inferred = items.filter((item, index) => index > previousEnd && index < nextStart);
    return inferred.length ? inferred : explicit;
  }

  // Focus trap utility
  const focusableSel = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function trapFocus(container, event) {
    const focusables = $$(focusableSel, container).filter(el => !el.hasAttribute('inert'));
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      last.focus();
      event.preventDefault();
    } else if (!event.shiftKey && document.activeElement === last) {
      first.focus();
      event.preventDefault();
    }
  }

  // Live region for announcements
  function announce(msg) {
    const region = $('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    setTimeout(() => { region.textContent = msg; }, 50);
  }
  window.LMannounce = announce;

  // Drawer (menu, cart, filters)
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      if (this._drawerReady) return;
      this._drawerReady = true;
      this.panel = $('.drawer__panel', this);
      this.backdrop = $('.drawer__backdrop', this);
      this.previouslyFocused = null;

      this.backdrop?.addEventListener('click', () => this.close());
      // The cart body is replaced after every AJAX update. Delegation keeps
      // freshly rendered close buttons working instead of retaining handlers
      // only on the first copy of the markup.
      this.addEventListener('click', (event) => {
        const closeTrigger = event.target.closest('[data-drawer-close]');
        if (closeTrigger && this.contains(closeTrigger)) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab' && this.hasAttribute('open')) trapFocus(this.panel, e);
      });
    }
    open() {
      this.previouslyFocused = document.activeElement;
      this.setAttribute('open', '');
      document.documentElement.classList.add('no-scroll');
      const first = $(focusableSel, this.panel);
      setTimeout(() => first?.focus(), 100);
      dispatch('lm:drawer:open', { drawer: this });
    }
    close() {
      this.removeAttribute('open');
      document.documentElement.classList.remove('no-scroll');
      this.previouslyFocused?.focus?.();
      dispatch('lm:drawer:close', { drawer: this });
    }
  }
  customElements.define('lm-drawer', DrawerElement);

  // Editorial carousel progress bars
  function initEditorialCarouselProgress() {
    $$('.lm-carousel-shell').forEach((shell) => {
      const track = $('[data-carousel-track]', shell);
      const progress = $('[data-carousel-progress]', shell) || $('.lm-progress span', shell);
      if (!track || !progress) return;
      const counter = $('[data-look-current]', shell);
      const items = $$('[data-carousel-item]', track);
      const update = () => {
        const max = Math.max(1, track.scrollWidth - track.clientWidth);
        const ratio = Math.max(0, Math.min(1, track.scrollLeft / max));
        const visibleRatio = Math.max(.18, Math.min(1, track.clientWidth / Math.max(track.scrollWidth, 1)));
        const travel = 100 - (visibleRatio * 100);
        progress.style.width = `${visibleRatio * 100}%`;
        progress.style.transform = `translateX(${ratio * travel / visibleRatio}%)`;
        // Índice legible ("03 / 19") en lugar de un identificador interno.
        if (counter && items.length) {
          const index = Math.round(ratio * (items.length - 1)) + 1;
          counter.textContent = String(index).padStart(2, '0');
        }
      };
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  }
  initEditorialCarouselProgress();



  // Keep the page vertically scrollable while the pointer is over horizontal
  // editorial carousels. Horizontal trackpad gestures still move the carousel.
  function initCarouselWheelPassthrough() {
    $$('[data-carousel-track]').forEach((track) => {
      if (track.dataset.wheelPassthrough === 'true') return;
      track.dataset.wheelPassthrough = 'true';
      track.addEventListener('wheel', (event) => {
        // Vertical wheel/trackpad gestures remain 100% native. Only an explicit
        // horizontal intent is captured by the carousel.
        const horizontalIntent = event.shiftKey || (Math.abs(event.deltaX) > Math.abs(event.deltaY) * 1.4 && Math.abs(event.deltaX) > 1);
        if (!horizontalIntent) return;
        event.preventDefault();
        const amount = event.shiftKey && Math.abs(event.deltaX) < 1 ? event.deltaY : event.deltaX;
        track.scrollLeft += amount;
      }, { passive: false });
    });
  }
  initCarouselWheelPassthrough();

  // Native horizontal scrolling stays in charge on touch devices. On desktop
  // this adds mouse drag without ever hijacking a vertical wheel gesture.
  function initCarouselPointerDrag() {
    $$('[data-carousel-track]').forEach((track) => {
      if (track.dataset.mouseDragReady === 'true') return;
      track.dataset.mouseDragReady = 'true';
      let active = false;
      let horizontal = false;
      let dragged = false;
      let startX = 0;
      let startY = 0;
      let startScroll = 0;

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        active = true;
        horizontal = false;
        dragged = false;
        startX = event.clientX;
        startY = event.clientY;
        startScroll = track.scrollLeft;
      });
      track.addEventListener('pointermove', (event) => {
        if (!active) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (!horizontal) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          if (Math.abs(dy) >= Math.abs(dx)) { active = false; return; }
          horizontal = true;
          track.setPointerCapture?.(event.pointerId);
        }
        event.preventDefault();
        dragged = Math.abs(dx) > 8;
        track.scrollLeft = startScroll - dx;
      });
      const end = (event) => {
        if (!active && !horizontal) return;
        active = false;
        horizontal = false;
        try { track.releasePointerCapture?.(event.pointerId); } catch {}
        window.setTimeout(() => { dragged = false; }, 80);
      };
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);
      track.addEventListener('click', (event) => {
        if (!dragged) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);
    });
  }
  initCarouselPointerDrag();

  // Optional slide-by-slide autoplay. It advances every 4–6 seconds instead
  // of continuously moving, pauses during manual interaction and respects
  // reduced-motion preferences.
  function initCarouselAutoplay() {
    $$('[data-carousel-autoplay]').forEach((shell) => {
      if (shell.dataset.autoplayReady === 'true') return;
      shell.dataset.autoplayReady = 'true';
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const track = $('[data-carousel-track]', shell);
      if (!track) return;
      const interval = Math.max(3000, Number(shell.dataset.carouselAutoplay || 4000));
      const loop = shell.dataset.carouselLoop === 'true';
      let timer = null;
      let visible = true;
      let pausedUntil = 0;

      const items = () => $$('[data-carousel-item]', track);
      const nearestIndex = (cards) => cards.reduce((best, item, index) => {
        const d = Math.abs(item.offsetLeft - track.scrollLeft - track.clientLeft);
        return d < best.distance ? { index, distance: d } : best;
      }, { index: 0, distance: Infinity }).index;
      const schedule = () => {
        window.clearTimeout(timer);
        if (!visible || document.hidden) return;
        timer = window.setTimeout(advance, interval);
      };
      const pause = (ms = 2800) => { pausedUntil = Date.now() + ms; schedule(); };
      const advance = () => {
        if (Date.now() < pausedUntil) { schedule(); return; }
        const cards = items();
        if (cards.length < 2) return;
        const current = nearestIndex(cards);
        let next = current + 1;
        if (next >= cards.length) next = loop ? 0 : cards.length - 1;
        const target = cards[next];
        const left = Math.max(0, target.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || 0));
        track.scrollTo({ left, behavior: 'smooth' });
        schedule();
      };

      ['pointerdown', 'touchstart', 'wheel', 'focusin'].forEach((name) =>
        shell.addEventListener(name, () => pause(3200), { passive: true }));
      shell.addEventListener('mouseenter', () => { pausedUntil = Number.POSITIVE_INFINITY; window.clearTimeout(timer); });
      shell.addEventListener('mouseleave', () => { pausedUntil = Date.now() + 1200; schedule(); });
      shell.addEventListener('focusout', () => { pausedUntil = Date.now() + 1200; schedule(); });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          visible = Boolean(entries[0]?.isIntersecting);
          schedule();
        }, { rootMargin: '100px' }).observe(shell);
      }
      document.addEventListener('visibilitychange', schedule);
      schedule();
    });
  }
  initCarouselAutoplay();
  document.addEventListener('shopify:section:load', () => { initCarouselPointerDrag(); initCarouselAutoplay(); });


  function initEditorialCollectionLinks() {
    const routePattern = /^\/collections\/(?:coleccion-|collection-)?(londres|marrakech|sevilla|bali|sandalias)\/?$/i;
    document.querySelectorAll('a[href]').forEach((link) => {
      let url;
      try { url = new URL(link.href, window.location.origin); } catch { return; }
      if (url.origin !== window.location.origin) return;
      const match = url.pathname.match(routePattern);
      if (!match) return;
      link.href = `/collections/all?lm_collection=${encodeURIComponent(match[1].toLowerCase())}`;
    });
  }
  initEditorialCollectionLinks();


  // Route editorial destination cards through /collections/all without 404s,
  // then keep only the imported products that belong to the requested Aura
  // destination and order the first products like the source collection.
  function initEditorialPseudoCollection() {
    const root = document.querySelector('.lm-aura-collection[data-collection-handle="all"]');
    if (!root) return;
    const destination = new URLSearchParams(window.location.search).get('lm_collection');
    if (!destination) return;
    const grid = root.querySelector('[data-collection-grid]');
    if (!grid) return;
    const normalize = (value) => String(value || '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const catalog = {
      sevilla: [
        'gardenia maxi halter', 'toledo rojo', 'toledo 4 colores', 'lucia conjunto top falda',
        'zahara azul', 'almudena', 'nora escote v', 'capri strapless floral', 'elisia volantes',
        'zamira', 'amara maxi', 'dayana cruzado', 'esther halter', 'shailine', 'shyann',
        'andrea flecos', 'cyranne', 'ziva', 'kiana', 'maia', 'minerva', 'ciara', 'xyrelle',
        'toledo estampado naranja marron'
      ],
      marrakech: [
        'toledo color marron', 'zahara azul', 'zahara', 'aitana', 'siena floral',
        'noa lazo cintura', 'toledo verde', 'gardenia amarillo', 'manila rojo', 'oasis rojo',
        'toledo terracota', 'toledo rojo', 'vivian burdeos', 'oasis burdeos', 'naima',
        'terracota claro', 'katerina', 'toledo estampado naranja marron', 'coral bloom',
        'brillo atardecer', 'indianna maxi', 'conjunto verona'
      ],
      londres: [
        'trench valentina rosa lunares', 'siena beige cuello pelo', 'charlene leopardo',
        'alessia encaje marfil', 'blazer verona cuadros taupe', 'torino efecto pelo marron',
        'valeria estructurada burdeos', 'roma efecto ante chocolate', 'trench valentina beige',
        'fiore floral', 'amelie burdeos tachuelas', 'riviera bandana azul', 'pantalon eva burdeos',
        'daisy bloom'
      ],
      bali: [
        'bruma vestido maxi encaje ruches', 'fay vestido largo encaje abertura',
        'solara vestido maxi lentejuelas volantes', 'florencia vestido maxi bicolor falda floral',
        'eternal bloom vestido midi corse', 'isabel vestido maxi estampado fiesta', 'katerina',
        'edith vestido largo tirantes', 'luz vestido volantes', 'rosalie maxi off shoulder encaje',
        'amaris vestido maxi malla', 'thirza vestido plisado multicolor'
      ],
      sandalias: [
        'sandalias venecia', 'sandalias doradas', 'sandalias elisetta', 'sandalias elara',
        'sandalias viviana', 'sandalias solea', 'sandalias bella', 'sandalias gioiella',
        'sandalias luminosas', 'sandalias luceria', 'sandalias doralia', 'sandalias aurabella',
        'bailarinas elegantes ante ribete metalizado', 'bailarinas texturizadas metalicas',
        'sandalias esmeralda', 'adriana sandalias'
      ]
    };
    const wanted = catalog[normalize(destination)];
    if (!wanted) return;
    const cards = Array.from(grid.querySelectorAll('.product-card[data-product-handle]'));
    if (!cards.length) return;
    const searchable = (card) => normalize(`${card.dataset.productHandle || ''} ${card.dataset.productTitle || ''}`);
    const unused = new Set(cards);
    const ordered = [];
    wanted.forEach((token) => {
      const key = normalize(token);
      const terms = key.split(/\s+/).filter(Boolean);
      const card = Array.from(unused).find((candidate) => {
        const haystack = searchable(candidate);
        const words = new Set(haystack.split(/\s+/).filter(Boolean));
        return terms.every((term) => words.has(term));
      });
      if (card) { ordered.push(card); unused.delete(card); }
    });
    if (!ordered.length) return;
    cards.forEach((card) => { card.hidden = true; card.style.display = 'none'; });
    ordered.forEach((card) => {
      card.hidden = false;
      card.style.removeProperty('display');
      grid.appendChild(card);
    });
    const labels = { londres: 'Londres', marrakech: 'Marrakech', sevilla: 'Sevilla', bali: 'Bali', sandalias: 'Sandalias' };
    const label = labels[normalize(destination)] || destination;
    const heading = root.querySelector('.collection-header__title');
    if (heading) heading.textContent = `Colección ${label}`;
    const breadcrumb = root.querySelector('.breadcrumbs [aria-current="page"]');
    if (breadcrumb) breadcrumb.textContent = `Colección ${label}`;
    document.title = `COLECCIÓN ${String(label).toUpperCase()} – ${document.title.split('–').pop()?.trim() || 'Alba Duarte'}`;
    const count = root.querySelector('[data-collection-count]');
    if (count) count.textContent = `${ordered.length} ${ordered.length === 1 ? 'artículo' : 'artículos'}`;
    root.querySelectorAll('form').forEach((form) => {
      if ((form.method || 'get').toLowerCase() !== 'get') return;
      if (form.querySelector('input[name="lm_collection"]')) return;
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = 'lm_collection'; input.value = normalize(destination); form.appendChild(input);
    });
    root.querySelectorAll('a[href]').forEach((link) => {
      const href = link.getAttribute('href') || '';
      if (!href.startsWith('/collections/all') || href.includes('lm_collection=')) return;
      const separator = href.includes('?') ? '&' : '?';
      link.setAttribute('href', `${href}${separator}lm_collection=${encodeURIComponent(normalize(destination))}`);
    });
    root.dataset.editorialDestination = normalize(destination);
  }
  initEditorialPseudoCollection();

  function initGiftOfferCountdowns() {
    $$('[data-gift-offer-timer]').forEach((root) => {
      if (root.dataset.timerReady === 'true') return;
      root.dataset.timerReady = 'true';
      const output = $('[data-gift-offer-time]', root);
      const copy = $('[data-gift-offer-timer-copy]', root);
      const duration = Math.max(1, Number(root.dataset.duration || 60)) * 1000;
      const deadline = Date.now() + duration;
      const tick = () => {
        const remaining = Math.max(0, deadline - Date.now());
        const totalSeconds = Math.ceil(remaining / 1000);
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        if (output) output.textContent = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        if (remaining <= 0) {
          if (copy) copy.textContent = 'Bolso gratis · reserva de 3 minutos finalizada';
          if (output) output.hidden = true;
          root.classList.add('is-expired');
          return;
        }
        window.setTimeout(tick, 250);
      };
      tick();
    });
  }
  initGiftOfferCountdowns();

  function initStockRotators() {
    $$('[data-stock-rotator]').forEach((root) => {
      if (root.dataset.stockReady === 'true') return;
      root.dataset.stockReady = 'true';
      const output = $('[data-stock-message]', root);
      const quantity = Number(root.dataset.stockQuantity || 0);
      if (!output) return;
      const low = root.dataset.stockMode === 'low' && quantity > 0;
      const unitLabel = quantity === 1 ? 'UNIDAD' : 'UNIDADES';
      const messages = low
        ? ['ÚLTIMAS UNIDADES EN STOCK', 'STOCK CASI AGOTADO', `QUEDAN ${quantity} ${unitLabel}`]
        : ['DISPONIBLE PARA ENVÍO', 'ENVÍO GRATIS A ESPAÑA', 'DEVOLUCIÓN 30 DÍAS'];
      let index = 0;
      window.setInterval(() => {
        index = (index + 1) % messages.length;
        output.textContent = messages[index];
      }, 3200);
    });
  }
  initStockRotators();


  function initDailyCountdowns() {
    $$('[data-daily-countdown]').forEach((root) => {
      if (root.dataset.countdownReady === 'true') return;
      root.dataset.countdownReady = 'true';
      const h = $('[data-hours]', root);
      const m = $('[data-minutes]', root);
      const sec = $('[data-seconds]', root);
      const date = $('[data-daily-date]', root);
      const tick = () => {
        const now = new Date();
        // Daily promotion: always count down to 23:59:59 of the shopper's current day.
        // This prevents a previous day's configured date from leaving the timer at 00:00:00.
        const end = new Date(now);
        end.setHours(23, 59, 59, 999);
        const diff = Math.max(0, end - now);
        const hours = Math.floor(diff / 3600000);
        const mins = Math.floor((diff % 3600000) / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        if (h) h.textContent = String(hours).padStart(2, '0');
        if (m) m.textContent = String(mins).padStart(2, '0');
        if (sec) sec.textContent = String(secs).padStart(2, '0');
        if (date) date.textContent = new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }).format(end);
      };
      tick();
      setInterval(tick, 1000);
    });
  }
  initDailyCountdowns();

  // Drawer triggers
  document.addEventListener('click', (e) => {
    const carouselButton = e.target.closest('[data-carousel-prev], [data-carousel-next]');
    if (carouselButton) {
      const shell = carouselButton.closest('.lm-carousel-shell');
      const track = shell?.querySelector('[data-carousel-track]');
      if (track) {
        const items = Array.from(track.querySelectorAll('[data-carousel-item], :scope > .lm-product-slide, :scope > .lm-collection-card, :scope > .lm-ugc-card'));
        if (items.length) {
          const current = items.reduce((best, item, index) => {
            const distance = Math.abs(item.offsetLeft - track.scrollLeft - track.clientLeft);
            return distance < best.distance ? { index, distance } : best;
          }, { index: 0, distance: Infinity }).index;
          const delta = carouselButton.hasAttribute('data-carousel-prev') ? -1 : 1;
          const targetIndex = Math.max(0, Math.min(items.length - 1, current + delta));
          const target = items[targetIndex];
          track.scrollTo({ left: target.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || 0), behavior: 'smooth' });
        }
      }
      return;
    }
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    e.preventDefault();
    const id = trigger.getAttribute('data-drawer-open');
    const drawer = document.getElementById(id);
    drawer?.open?.();
  });



  // Collection grid/list view switcher.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-collection-view]');
    if (!btn) return;
    const section = btn.closest('[data-collection-section]');
    const grid = section?.querySelector('[data-collection-grid]');
    if (!grid) return;
    const mode = btn.dataset.collectionView;
    grid.classList.add('collection-grid--switching');
    window.setTimeout(() => { grid.classList.toggle('collection-grid--single', mode === 'single'); }, 90);
    window.setTimeout(() => { grid.classList.remove('collection-grid--switching'); }, 320);
    section.querySelectorAll('[data-collection-view]').forEach((control) => control.setAttribute('aria-pressed', String(control === btn)));
    try { localStorage.setItem('lm:collection-view', mode); } catch {}
  });
  $$('[data-collection-section]').forEach((section) => {
    let mode = 'grid';
    try { mode = localStorage.getItem('lm:collection-view') || 'grid'; } catch {}
    const grid = section.querySelector('[data-collection-grid]');
    if (!grid) return;
    grid.classList.toggle('collection-grid--single', mode === 'single');
    section.querySelectorAll('[data-collection-view]').forEach((control) => control.setAttribute('aria-pressed', String(control.dataset.collectionView === mode)));
  });

  document.addEventListener('click', (e) => {
    const reopen = e.target.closest('[data-popup-reopen]');
    if (!reopen) return;
    const popup = document.querySelector('lm-popup');
    popup?.show?.();
  });

  // Mobile nav submenu accordion
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-mobile-submenu-toggle]');
    if (!toggle) return;
    e.preventDefault();
    const item = toggle.closest('.mobile-nav__item');
    const submenu = $('.mobile-nav__submenu', item);
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    if (submenu) submenu.hidden = expanded;
  });

  // Sticky header. La altura NUNCA cambia: al bajar sólo aparece el fondo
  // blanco (transición de background-color), así que no hay layout shift ni
  // parpadeo. El header permanece siempre accesible, no se oculta.
  const header = $('[data-site-header]');
  if (header) {
    let ticking = false;
    const transparentMode = header.getAttribute('data-transparent') === 'true';
    // Umbral bajo: el blanco empieza a entrar en cuanto el usuario se mueve,
    // como en la referencia. Con histéresis para que no oscile en el borde.
    const ON = 12;
    const OFF = 4;
    let scrolledState = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        if (!scrolledState && y > ON) {
          scrolledState = true;
          header.classList.add('site-header--scrolled');
        } else if (scrolledState && y < OFF) {
          scrolledState = false;
          header.classList.remove('site-header--scrolled');
        }
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // El solape header ↔ hero lo resuelve el CSS con --af-header-h. Aquí sólo
    // se marca el hero; ya no se mide ni se escribe marginTop por JS (esa
    // medición provocaba el salto al cargar las fuentes).
    if (transparentMode) {
      $('main .hero')?.classList.add('hero--overlap-header');
    }
  }

  // Tarjetas con segunda imagen. En desktop el cambio lo resuelve CSS;
  // en touch no se secuestra un tap vertical: las colecciones usan sus
  // controles de galería y el enlace de la tarjeta conserva su comportamiento.
  function initCardHoverMedia(root = document) {
    $$('.product-card', root).forEach((card) => {
      const secondary = $('.product-card__media img:nth-of-type(2)', card);
      const media = $('.product-card__media', card);
      if (!secondary || !media) return;

      card.classList.add('product-card--has-hover');
      if (card.dataset.touchPreviewReady === 'true') return;
      card.dataset.touchPreviewReady = 'true';

      // Touch preview: al apoyar el dedo se muestra inmediatamente la segunda
      // foto. Los listeners son pasivos y jamás llaman preventDefault(), por lo
      // que el scroll vertical de la página y el swipe horizontal del carrusel
      // siguen siendo completamente nativos.
      let touchPreviewTimer = null;
      let touchStartX = 0;
      let touchStartY = 0;
      let touchMoved = false;

      const revealAlt = () => {
        if (card.classList.contains('is-gallery-switching')) return;
        window.clearTimeout(touchPreviewTimer);
        card.classList.add('is-showing-alt');
      };
      const restorePrimary = (delay = 0) => {
        window.clearTimeout(touchPreviewTimer);
        touchPreviewTimer = window.setTimeout(() => card.classList.remove('is-showing-alt'), delay);
      };

      media.addEventListener('touchstart', (event) => {
        const touch = event.touches?.[0];
        if (!touch) return;
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        touchMoved = false;
        revealAlt();
      }, { passive: true });

      media.addEventListener('touchmove', (event) => {
        const touch = event.touches?.[0];
        if (!touch) return;
        const dx = touch.clientX - touchStartX;
        const dy = touch.clientY - touchStartY;
        if (Math.hypot(dx, dy) > 8) {
          touchMoved = true;
          // Mantener la segunda imagen mientras el dedo siga sobre la card.
          // No usamos preventDefault(), así que el scroll vertical sigue siendo nativo.
        }
      }, { passive: true });

      media.addEventListener('touchend', () => restorePrimary(touchMoved ? 0 : 320), { passive: true });
      media.addEventListener('touchcancel', () => restorePrimary(0), { passive: true });

      // Pen/stylus: mismo comportamiento de previsualización sin secuestrar el
      // gesto. Mouse queda exclusivamente en manos del :hover de CSS.
      media.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'pen') revealAlt();
      }, { passive: true });
      media.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'pen') restorePrimary(240);
      }, { passive: true });
      media.addEventListener('pointercancel', (event) => {
        if (event.pointerType === 'pen') restorePrimary(0);
      }, { passive: true });
      media.addEventListener('dragstart', (event) => event.preventDefault());
    });
  }
  initCardHoverMedia();
  document.addEventListener('shopify:section:load', (event) => initCardHoverMedia(event.target));

  // Quantity input
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('.quantity-input__field', this);
      this.minusBtn = $('[data-quantity-minus]', this);
      this.plusBtn = $('[data-quantity-plus]', this);
      this.minusBtn?.addEventListener('click', () => this.changeBy(-1));
      this.plusBtn?.addEventListener('click', () => this.changeBy(1));
      this.input?.addEventListener('change', () => this.normalize());
    }
    changeBy(delta) {
      const cur = parseInt(this.input.value || '0', 10);
      const min = parseInt(this.input.min || '0', 10);
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const next = Math.max(min, Math.min(max, cur + delta));
      if (next === cur) return;
      this.input.value = next;
      this.dispatchChange();
    }
    dispatchChange() {
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    normalize() {
      const min = parseInt(this.input.min || '0', 10);
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const current = parseInt(this.input.value || String(min), 10);
      this.input.value = String(Math.max(min, Math.min(max, Number.isFinite(current) ? current : min)));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  // Multi-variant bundle configurator. Every dress resolves to a real,
  // available variant ID before the form can be submitted.
  class VolumeOffers extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.quantityInput = $('.quantity-input__field[name="quantity"]', this.form);
      this.options = $$('[data-volume-option]', this);
      this.radios = $$('input[type="radio"]', this);
      this.currency = this.dataset.currency || 'EUR';
      this.price = Number(this.dataset.price || 0);
      this.savingTwo = Number(this.dataset.savingTwo || 500);
      this.savingThree = Number(this.dataset.savingThree || 1000);
      this.optimized = this.dataset.optimized === 'true';
      this.cartProtectionEligible = this.dataset.cartProtectionEligible === 'true';
      this.discountsVerified = this.dataset.discountsVerified === 'true';
      this._syncing = false;
      this.customizer = $('[data-volume-customizer]', this);
      this.units = $$('[data-bundle-unit]', this);
      this.pickers = $$('[data-bundle-picker]', this);
      this.protection = $('[data-shipping-protection]', this);
      this.protectionInput = $('[data-shipping-protection-checkbox]', this);
      this.protectionTitle = $('[data-protection-title]', this);
      this.protectionHelp = $('[data-protection-help]', this);
      this.protectionPrice = $('[data-protection-price]', this);
      this.protectionIncludedBadge = $('[data-protection-included]', this);
      this.protectionToggle = $('[data-protection-toggle]', this);
      this.protectionVariantId = Number(this.dataset.protectionVariantId || 0);
      this.protectionExpectedPrice = Number(this.dataset.protectionPrice || 395);
      this.protectionReady = false;
      if (this.protectionInput) this.protectionInput.checked = false;
      if (this.protection) {
        this.protection.hidden = true;
        this.validateProtectionVariant();
      }
      try { this.variants = JSON.parse($('[data-product-json]', this.form)?.textContent || '[]'); }
      catch { this.variants = []; }
      this.radios.forEach(radio => radio.addEventListener('change', () => {
        if (radio.checked) this.selectQuantity(Number(radio.value), true);
      }));
      $$('[data-bundle-option]', this).forEach(select => select.addEventListener('change', () => this.onBundleOptionChange(select)));
      this.pickers.forEach(picker => {
        const trigger = $('[data-bundle-trigger]', picker);
        trigger?.addEventListener('click', event => {
          event.stopPropagation();
          const willOpen = $('[data-bundle-menu]', picker)?.hidden;
          this.closePickers();
          if (willOpen) this.openPicker(picker);
        });
        $$('[data-bundle-value]', picker).forEach(option => option.addEventListener('click', () => {
          if (option.disabled) return;
          const select = $('[data-bundle-option]', picker);
          select.value = option.dataset.bundleValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          this.closePickers();
        }));
      });
      this._closePickers = event => { if (!this.contains(event.target)) this.closePickers(); };
      this._pickerKeydown = event => { if (event.key === 'Escape') this.closePickers(true); };
      document.addEventListener('click', this._closePickers);
      document.addEventListener('keydown', this._pickerKeydown);
      document.addEventListener('lm:variant:change', e => {
        if (e.detail?.source?.closest('form') === this.form) this.updateVariant(e.detail.variant);
      });
      const initialId = $('[name="id"]', this.form)?.value;
      const initial = this.variants.find(v => String(v.id) === String(initialId)) || this.variants.find(v => v.available);
      this.baseVariant = initial;
      this.units.forEach(unit => this.setUnitVariant(unit, initial));
      this.selectQuantity(Number(this.dataset.defaultQuantity || 1), true);
    }
    disconnectedCallback() {
      document.removeEventListener('click', this._closePickers);
      document.removeEventListener('keydown', this._pickerKeydown);
    }
    openPicker(picker) {
      const menu = $('[data-bundle-menu]', picker);
      const trigger = $('[data-bundle-trigger]', picker);
      if (!menu || !trigger) return;
      menu.hidden = false;
      picker.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      $('.volume-picker__option.is-selected:not(:disabled)', menu)?.focus();
    }
    closePickers(restoreFocus = false) {
      this.pickers.forEach(picker => {
        const wasOpen = picker.classList.contains('is-open');
        picker.classList.remove('is-open');
        const menu = $('[data-bundle-menu]', picker);
        const trigger = $('[data-bundle-trigger]', picker);
        if (menu) menu.hidden = true;
        trigger?.setAttribute('aria-expanded', 'false');
        if (wasOpen && restoreFocus) trigger?.focus();
      });
    }
    formatMoney(cents) {
      try {
        if (String(this.currency).toUpperCase() === 'EUR') {
          return `${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100)} €`;
        }
        return new Intl.NumberFormat(document.documentElement.lang || 'es-ES', { style: 'currency', currency: this.currency, currencyDisplay: 'symbol' }).format(cents / 100);
      } catch { return `${(cents / 100).toFixed(2).replace('.', ',')} ${this.currency}`; }
    }
    async validateProtectionVariant() {
      if (!this.protection) return;
      if (this.protectionVariantId) {
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}variants/${this.protectionVariantId}.js`, { headers: { Accept: 'application/json' } });
          const variant = await response.json();
          this.protectionReady = response.ok && variant.available !== false && Number(variant.price) === this.protectionExpectedPrice;
        } catch { this.protectionReady = false; }
      }
      if (!this.protectionReady && this.protectionInput) this.protectionInput.checked = false;
      this.updateProtectionState(this.currentQuantity());
    }
    updateProtectionState(quantity = this.currentQuantity(), resetOptional = false) {
      if (!this.protection) return;
      const included = quantity === 2;
      const wasIncluded = this.protection.classList.contains('is-included');
      this.protection.classList.toggle('is-included', included);
      this.protection.hidden = included ? false : !this.protectionReady;
      if (this.protectionTitle) this.protectionTitle.textContent = included ? 'Protección de envío incluida' : 'Protección de envío';
      if (this.protectionHelp) this.protectionHelp.textContent = included
        ? 'La oferta popular protege tu pedido sin coste adicional. Ahorras 3,95 €.'
        : 'Extra opcional para proteger este pedido ante incidencias durante el transporte.';
      if (this.protectionPrice) this.protectionPrice.hidden = included;
      if (this.protectionIncludedBadge) this.protectionIncludedBadge.hidden = !included;
      if (this.protectionToggle) this.protectionToggle.hidden = included;
      if (this.protectionInput) {
        this.protectionInput.disabled = included;
        if (included) this.protectionInput.checked = true;
        else if (resetOptional || wasIncluded) this.protectionInput.checked = false;
      }
    }
    currentQuantity() {
      return Number(this.radios.find(radio => radio.checked)?.value || 1);
    }
    savingFor(quantity, regular = this.price * quantity) {
      if (!this.discountsVerified) return 0;
      return quantity === 2 ? this.savingTwo : quantity === 3 ? this.savingThree : 0;
    }
    updateVariant(variant) {
      this.price = Number(variant.price || 0);
      this.baseVariant = variant;
      if (this.currentQuantity() === 3) {
        this.units.slice(0, 3).forEach(unit => {
          $$('[data-bundle-field]', unit).forEach(field => {
            if (field.dataset.bundleColor === 'true') return;
            const select = $('[data-bundle-option]', field);
            const optionIndex = Number(select?.dataset.bundleOption);
            if (select && variant.options[optionIndex] != null) select.value = variant.options[optionIndex];
          });
          const resolved = this.selectedVariant(unit);
          if (resolved) this.setUnitVariant(unit, resolved);
        });
      } else if (this.currentQuantity() !== 2) {
        this.setUnitVariant(this.units[0], variant);
      }
      this.updatePricing();
    }
    selectedOptions(unit) {
      return $$('[data-bundle-option]', unit).map(select => select.value);
    }
    selectedVariant(unit) {
      const selected = this.selectedOptions(unit);
      return this.variants.find(variant => variant.options.every((value, index) => value === selected[index]));
    }
    setUnitVariant(unit, variant) {
      if (!unit || !variant) return;
      $$('[data-bundle-option]', unit).forEach((select, index) => { select.value = variant.options[index] ?? ''; });
      unit.dataset.variantId = variant.id;
      this.updateUnitAvailability(unit);
      this.syncCustomPickers(unit);
      const error = $('[data-bundle-error]', unit);
      if (error) error.hidden = !!variant.available;
    }
    updateUnitAvailability(unit) {
      $$('[data-bundle-option]', unit).forEach((select, optionIndex) => {
        Array.from(select.options).forEach(option => {
          // Keep every value that belongs to at least one real available
          // variant reachable. If the other field is incompatible, changing
          // this value moves it to the first valid combination automatically.
          option.disabled = !this.variants.some(variant => variant.available && variant.options[optionIndex] === option.value);
          const picker = select.closest('[data-bundle-picker]');
          const pickerOption = picker ? $(`[data-bundle-value="${CSS.escape(option.value)}"]`, picker) : null;
          if (pickerOption) {
            pickerOption.disabled = option.disabled;
            pickerOption.setAttribute('aria-disabled', String(option.disabled));
          }
        });
      });
    }
    onBundleOptionChange(select) {
      const unit = select.closest('[data-bundle-unit]');
      const changedIndex = Number(select.dataset.bundleOption);
      const changedValue = select.value;
      let variant = this.selectedVariant(unit);
      if (!variant?.available && this.currentQuantity() !== 3) {
        variant = this.variants.find(item => item.available && item.options[changedIndex] === changedValue);
      }
      if (variant) this.setUnitVariant(unit, variant);
      this.validateSelection();
      this.updatePricing();
    }
    syncCustomPickers(unit) {
      $$('[data-bundle-picker]', unit).forEach(picker => {
        const select = $('[data-bundle-option]', picker);
        const label = $('[data-bundle-label]', picker);
        const triggerSwatch = $('[data-bundle-swatch]', picker);
        if (label) label.textContent = select.value;
        $$('[data-bundle-value]', picker).forEach(option => {
          const selected = option.dataset.bundleValue === select.value;
          option.classList.toggle('is-selected', selected);
          option.setAttribute('aria-selected', String(selected));
          if (selected && triggerSwatch) {
            const optionSwatch = $('.volume-picker__swatch', option);
            if (optionSwatch) triggerSwatch.style.cssText = optionSwatch.style.cssText;
          }
        });
      });
    }
    activeVariants() {
      const quantity = this.currentQuantity();
      if (quantity === 2 || quantity === 3) return this.units.slice(0, quantity).map(unit => this.selectedVariant(unit));
      return Array.from({ length: quantity }, () => this.baseVariant);
    }
    validateSelection() {
      const variants = this.activeVariants();
      const counts = new Map();
      variants.forEach(variant => { if (variant) counts.set(String(variant.id), (counts.get(String(variant.id)) || 0) + 1); });
      let valid = variants.length === this.currentQuantity() && variants.every(variant => {
        if (!variant?.available) return false;
        if (variant.inventory_management && variant.inventory_policy !== 'continue' && counts.get(String(variant.id)) > Number(variant.inventory_quantity || 0)) return false;
        if (variant.quantity_rule?.max != null && counts.get(String(variant.id)) > Number(variant.quantity_rule.max)) return false;
        return true;
      });
      const button = $('[data-atc-btn]', this.form);
      if (button) button.disabled = !valid;
      const status = $('[data-customizer-status]', this);
      if (status) status.textContent = valid ? 'Combinación disponible' : 'Revisa la disponibilidad';
      return valid;
    }
    updatePricing() {
      this.options.forEach(option => {
        const quantity = Number(option.dataset.quantity);
        const chosen = quantity === 2 || quantity === 3 ? this.units.slice(0, quantity).map(unit => this.selectedVariant(unit)) : Array.from({ length: quantity }, () => this.baseVariant);
        const regular = chosen.every(Boolean) ? chosen.reduce((sum, variant) => sum + Number(variant.price || 0), 0) : this.price * quantity;
        const saving = this.savingFor(quantity, regular);
        const total = Math.max(0, regular - saving);
        const totalEl = $('[data-offer-total]', option);
        const compareEl = $('[data-offer-compare]', option);
        const unitEl = $('[data-offer-unit]', option);
        const savingEl = $('[data-offer-saving]', option);
        if (totalEl) totalEl.textContent = this.formatMoney(total);
        if (compareEl) { compareEl.textContent = this.formatMoney(regular); compareEl.hidden = saving === 0; }
        if (unitEl) unitEl.textContent = this.formatMoney(Math.round(total / quantity));
        if (savingEl) { savingEl.textContent = `Ahorras ${this.formatMoney(saving)}`; savingEl.hidden = saving === 0; }
      });
    }
    selectQuantity(quantity, updateInput) {
      this._syncing = true;
      this.radios.forEach(radio => { radio.checked = Number(radio.value) === quantity; });
      this.options.forEach(option => option.classList.toggle('volume-offer--selected', Number(option.dataset.quantity) === quantity));
      this.updateProtectionState(quantity, true);
      if (updateInput && this.quantityInput && quantity > 0) this.quantityInput.value = String(quantity);
      const customizable = quantity === 2 || quantity === 3;
      if (this.customizer) this.customizer.hidden = !customizable;
      this.units.forEach((unit, index) => {
        unit.hidden = !customizable || index >= quantity;
        $$('[data-bundle-field]', unit).forEach(field => {
          const lockSize = quantity === 3 && field.dataset.bundleColor !== 'true';
          field.hidden = lockSize;
          if (lockSize && this.baseVariant) {
            const select = $('[data-bundle-option]', field);
            const optionIndex = Number(select?.dataset.bundleOption);
            if (select && this.baseVariant.options[optionIndex] != null) select.value = this.baseVariant.options[optionIndex];
          }
        });
        if (!unit.hidden) {
          const variant = this.selectedVariant(unit);
          if (variant) this.setUnitVariant(unit, variant);
        }
      });
      const title = $('[data-customizer-title]', this);
      const kicker = $('[data-customizer-kicker]', this);
      const lead = $('[data-customizer-lead]', this);
      if (title) title.textContent = quantity === 3 ? 'Combina los colores de tus 3 vestidos' : 'Combina tus 2 vestidos';
      if (kicker) kicker.textContent = quantity === 3 ? 'PERSONALIZA SOLO LOS COLORES' : 'EXCLUSIVO EN EL PACK DE 2';
      if (lead) lead.textContent = quantity === 3 ? 'Elige el color de cada vestido. Las 3 unidades mantienen la talla principal seleccionada.' : 'Selecciona el color y la talla de cada vestido por separado.';
      this.closePickers();
      this.validateSelection();
      this.updatePricing();
      this.dispatchEvent(new CustomEvent('lm:offer:change', { bubbles: true, detail: { quantity } }));
      this._syncing = false;
    }
    getCartItems() {
      if (!this.validateSelection()) return null;
      const quantity = this.currentQuantity();
      const paidProtection = this.protectionSelected();
      const includedProtection = this.protectionIncluded();
      const group = paidProtection ? `lm-${Date.now()}-${window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}` : '';
      const items = this.activeVariants().map(variant => {
        const properties = { '_Oferta Alba Duarte': `${quantity} unidades` };
        if (this.cartProtectionEligible) properties._lm_shipping_protection_eligible = 'true';
        if (includedProtection) properties._lm_protection_included = 'true';
        if (paidProtection) {
          properties._lm_protection_group = group;
          properties._lm_protection_role = 'main';
        }
        return { id: Number(variant.id), quantity: 1, properties };
      });
      if (paidProtection) {
        items.push({ id: this.protectionVariantId, quantity: 1, properties: { '_lm_protection_group': group, '_lm_protection_role': 'protection' } });
      }
      return items;
    }
    protectionIncluded() {
      return !!this.protection && !this.optimized && this.currentQuantity() === 2;
    }
    protectionSelected() {
      return this.currentQuantity() !== 2 && this.protectionReady && !!this.protectionInput?.checked;
    }
    getProtectionCartItems() {
      return this.protectionSelected() ? this.getCartItems() : null;
    }
  }
  customElements.define('volume-offers', VolumeOffers);

  class EditorialSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = $$('[data-editorial-slide]', this);
      this.dots = $$('[data-editorial-slide-to]', this);
      this.prevButton = $('[data-editorial-prev]', this);
      this.nextButton = $('[data-editorial-next]', this);
      this.index = 0;
      this.interval = Math.max(5000, Number(this.dataset.interval || 6000));
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.dots.forEach((dot) => dot.addEventListener('click', () => {
        this.show(Number(dot.dataset.editorialSlideTo || 0));
        this.restart();
      }));
      this.nextButton?.addEventListener('click', () => {
        this.show(this.index + 1);
        this.restart();
      });
      this.prevButton?.addEventListener('click', () => {
        this.show(this.index - 1);
        this.restart();
      });
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.visibilityHandler = () => document.hidden ? this.stop() : this.start();
      document.addEventListener('visibilitychange', this.visibilityHandler);
      this.preloadNext();
      this.start();
    }
    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    show(index) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
      this.preloadNext();
    }
    preloadNext() {
      if (this.slides.length < 2) return;
      const nextSlide = this.slides[(this.index + 1) % this.slides.length];
      const nextImage = $('img', nextSlide);
      if (!nextImage) return;
      nextImage.loading = 'eager';
      nextImage.decode?.().catch(() => {});
    }
    start() {
      if (this.reducedMotion || document.hidden || this.slides.length < 2 || this.timer) return;
      this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
    }
    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }
    restart() { this.stop(); this.start(); }
  }
  customElements.define('editorial-slideshow', EditorialSlideshow);

  // Predictive search modal
  const searchBtn = $('[data-open-search]');
  const searchModal = $('#SearchModal');
  if (searchBtn && searchModal) {
    searchBtn.addEventListener('click', (e) => { e.preventDefault(); searchModal.setAttribute('open',''); document.documentElement.classList.add('no-scroll'); setTimeout(() => $('input[type="search"]', searchModal)?.focus(), 60); });
    $$('[data-search-close]', searchModal).forEach(b => b.addEventListener('click', () => { searchModal.removeAttribute('open'); document.documentElement.classList.remove('no-scroll'); }));
    searchModal.addEventListener('keydown', (e) => { if (e.key === 'Escape') { searchModal.removeAttribute('open'); document.documentElement.classList.remove('no-scroll'); } });
  }

  // Predictive search results
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-predictive-results]', this);
      this.abortCtrl = null;
      this.debouncer = null;
      this.input?.addEventListener('input', () => this.onInput());
    }
    onInput() {
      clearTimeout(this.debouncer);
      const term = this.input.value.trim();
      if (term.length < 2) { this.results.innerHTML = ''; return; }
      this.debouncer = setTimeout(() => this.query(term), 220);
    }
    async query(term) {
      if (this.abortCtrl) this.abortCtrl.abort();
      this.abortCtrl = new AbortController();
      try {
        const url = `${window.Shopify?.routes?.root || '/'}search/suggest?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article&resources[limit]=8&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.abortCtrl.signal });
        if (!res.ok) throw new Error('search failed');
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const frag = doc.querySelector('[data-predictive-results]');
        if (frag) this.results.innerHTML = frag.innerHTML;
      } catch (err) {
        if (err.name !== 'AbortError') console.warn(err);
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  // Variant picker
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form') || $('form', this);
      this.productJsonEl = $('[data-product-json]', this) || $('[data-product-json="' + this.dataset.productHandle + '"]');
      this.variants = [];
      try {
        this.variants = JSON.parse(this.productJsonEl?.textContent || '[]');
      } catch (e) { this.variants = []; }
      this.optionInputs = $$('input[name^="options["], select[name^="options["]', this);
      this.optionInputs.forEach(input => input.addEventListener('change', () => this.onOptionChange()));
      // Mobile Safari can occasionally miss the radio `change` when the
      // absolutely-positioned swatch ring receives the tap. Resolve the
      // touched swatch explicitly and run the same variant update path.
      const colorGroup = $('.variant-option[data-option-type="color"]', this);
      colorGroup?.addEventListener('pointerup', event => {
        const swatch = event.target.closest('.variant-option__swatch');
        const input = swatch ? $('input[type="radio"]', swatch) : null;
        if (!input || input.checked) {
          if (input) this.updateMediaForColor(input.value);
          return;
        }
        input.checked = true;
        this.onOptionChange();
      }, { passive: true });
      queueMicrotask(() => {
        const id = $('input[name="id"]', this.form)?.value;
        const initial = this.variants.find(v => String(v.id) === String(id));
        if (initial) this.updateMedia(initial);
      });
    }
    onOptionChange() {
      const selected = this.getSelectedOptions();
      const match = this.variants.find(v => v.options.every((o, i) => o === selected[i]));
      const idInput = $('input[name="id"]', this.form);
      const submitBtn = $('[data-atc-btn]', this.form);
      const selectedLabels = $$('.variant-option__value-selected', this);
      selectedLabels.forEach(el => { const group = el.closest('.variant-option'); const idx = parseInt(group?.dataset.optionIndex || '0', 10); el.textContent = selected[idx] || ''; });
      this.updateAvailability(selected);
      // Change the gallery even when the current size/color combination does
      // not resolve to a purchasable variant. The colour choice itself is the
      // authoritative source for theme-owned variant media.
      this.updateMediaForColor(this.getSelectedColor(), match);
      if (match) {
        if (idInput) idInput.value = match.id;
        if (submitBtn) {
          submitBtn.disabled = !match.available;
          submitBtn.textContent = match.available
            ? (submitBtn.dataset.labelAvailable || 'Añadir al carrito')
            : (submitBtn.dataset.labelSoldOut || 'Agotado');
        }
        if (match.price != null) {
          this.refreshPriceBlock(match.id);
          this.dispatchEvent(new CustomEvent('lm:variant:change', { bubbles: true, detail: { variant: match, color: this.getSelectedColor(), source: this } }));
        }
        // Update URL
        if (history.replaceState) {
          const url = new URL(window.location);
          url.searchParams.set('variant', match.id);
          history.replaceState({}, '', url);
        }
      } else if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = submitBtn.dataset.labelUnavailable || 'No disponible';
      }
    }
    getSelectedOptions() {
      const map = new Map();
      this.optionInputs.forEach(input => {
        if (input.type === 'radio' && !input.checked) return;
        const name = input.name.match(/options\[([^\]]+)\]/)?.[1];
        const idx = parseInt(input.dataset.optionIndex || '0', 10);
        map.set(idx, input.value);
      });
      const arr = [];
      for (let i = 0; i < 3; i++) if (map.has(i)) arr.push(map.get(i));
      return arr;
    }
    getSelectedColor() {
      const group = $('.variant-option[data-option-type="color"]', this);
      return group ? $('input:checked', group)?.value || '' : '';
    }
    getMediaContext() {
      const group = $('.variant-option[data-option-type="color"]', this);
      if (!group) return { colors: [], featuredByColor: {} };
      const colorIndex = Number(group.dataset.optionIndex || 0);
      const colors = $$('input[type="radio"]', group).map(input => input.value);
      const featuredByColor = {};
      this.variants.forEach(variant => {
        const color = variant.options[colorIndex];
        if (color && variant.featured_media && !featuredByColor[normalizeToken(color)]) featuredByColor[normalizeToken(color)] = variant.featured_media;
      });
      return { colors, featuredByColor };
    }
    updateMedia(variant) {
      this.updateMediaForColor(this.getSelectedColor(), variant);
    }
    updateMediaForColor(color, variant = null) {
      const scope = this.closest('.product, .featured-product') || document;
      const context = this.getMediaContext();
      const gallery = $('[data-product-gallery]', scope);
      gallery?.selectVariantMedia?.(variant?.featured_media || null, color, context);
      const grid = $('[data-variant-media-grid]', scope);
      grid?.selectVariantMedia?.(variant?.featured_media || null, color, context);
    }
    updateAvailability(selected) {
      $$('.variant-option', this).forEach(group => {
        const idx = parseInt(group.dataset.optionIndex, 10);
        $$('.variant-option__swatch, .variant-option__button', group).forEach(item => {
          const val = $('input', item)?.value;
          const isAvailable = this.variants.some(v =>
            v.available &&
            v.options[idx] === val &&
            v.options.every((o, i) => i === idx || selected[i] == null || o === selected[i])
          );
          item.toggleAttribute('data-unavailable', !isAvailable);
        });
        const select = $('select[name^="options["]', group);
        if (select) {
          Array.from(select.options).forEach(option => {
            option.disabled = !this.variants.some(v =>
              v.available &&
              v.options[idx] === option.value &&
              v.options.every((o, i) => i === idx || selected[i] == null || o === selected[i])
            );
          });
        }
      });
    }
    async refreshPriceBlock(variantId) {
      try {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return;
        const url = `${location.pathname}?variant=${variantId}&section_id=${encodeURIComponent(sectionId)}`;
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-price-block]');
        const target = this.closest('.product-info, .featured-product__content')?.querySelector('[data-price-block]');
        if (fresh && target) target.innerHTML = fresh.innerHTML;
      } catch (e) {}
    }
  }
  customElements.define('variant-picker', VariantPicker);

  // Product gallery
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.product-gallery__slide', this);
      this.thumbs = $$('.product-gallery__thumb', this);
      this.prevBtn = $('[data-prev]', this);
      this.nextBtn = $('[data-next]', this);
      this.thumbs.forEach(t => t.addEventListener('click', () => this.goToMedia(t.dataset.mediaId)));
      this.prevBtn?.addEventListener('click', () => this.step(-1));
      this.nextBtn?.addEventListener('click', () => this.step(1));
      const surface = $('.product-gallery__main', this);
      if (surface) {
        let startX = null;
        let startY = null;
        surface.addEventListener('touchstart', (event) => {
          const touch = event.touches?.[0];
          if (!touch) return;
          startX = touch.clientX; startY = touch.clientY;
        }, { passive: true });
        surface.addEventListener('touchend', (event) => {
          if (startX == null || startY == null) return;
          const touch = event.changedTouches?.[0];
          if (!touch) return;
          const dx = touch.clientX - startX;
          const dy = touch.clientY - startY;
          startX = null; startY = null;
          if (Math.abs(dx) < 42 || Math.abs(dx) <= Math.abs(dy) * 1.15) return;
          this.step(dx < 0 ? 1 : -1);
        }, { passive: true });
      }
    }
    currentIndex() {
      return this.slides.findIndex(s => s.dataset.active === 'true');
    }
    visibleIndexes() { return this.slides.map((slide, index) => slide.hidden ? -1 : index).filter(index => index >= 0); }
    goTo(i, direction = 0) {
      if (!this.slides.length) return;
      const visible = this.visibleIndexes();
      if (!visible.length) return;
      const idx = visible.includes(i) ? i : visible[0];
      const oldIdx = this.currentIndex();
      if (idx === oldIdx) return;
      const dir = direction || (idx > oldIdx ? 1 : -1);
      const oldSlide = this.slides[oldIdx];
      const newSlide = this.slides[idx];
      this.slides.forEach(slide => slide.classList.remove('lm-slide-in-next','lm-slide-in-prev','lm-slide-out-next','lm-slide-out-prev'));
      if (oldSlide) oldSlide.classList.add(dir > 0 ? 'lm-slide-out-next' : 'lm-slide-out-prev');
      if (newSlide) newSlide.classList.add(dir > 0 ? 'lm-slide-in-next' : 'lm-slide-in-prev');
      this.slides.forEach((s, k) => s.dataset.active = k === idx ? 'true' : 'false');
      this.thumbs.forEach((t, k) => t.setAttribute('aria-current', k === idx ? 'true' : 'false'));
      window.setTimeout(() => {
        oldSlide?.classList.remove('lm-slide-out-next','lm-slide-out-prev');
        newSlide?.classList.remove('lm-slide-in-next','lm-slide-in-prev');
      }, 420);
    }
    step(delta) {
      const visible = this.visibleIndexes();
      if (!visible.length) return;
      const current = Math.max(0, visible.indexOf(this.currentIndex()));
      this.goTo(visible[(current + delta + visible.length) % visible.length], delta);
    }
    goToMedia(mediaId) {
      const index = this.slides.findIndex(slide => String(slide.dataset.mediaId) === String(mediaId));
      if (index >= 0 && !this.slides[index].hidden) this.goTo(index, index >= this.currentIndex() ? 1 : -1);
    }
    selectVariantMedia(featuredMedia, color, context) {
      const matchingSlides = mediaCandidates(this.slides, color, context);
      const matchingIds = matchingSlides.map(slide => slide.dataset.mediaId);
      const featuredSlide = this.slides.find(slide => itemMatchesFeatured(slide, featuredMedia));
      const namedColorSlide = matchingSlides.find(slide => mediaMatchesColor(slide.dataset.mediaToken, color));
      const hasNamedColorMatch = Boolean(namedColorSlide);
      const featuredMatchesColor = featuredSlide && mediaMatchesColor(featuredSlide.dataset.mediaToken, color);
      const trustFeatured = featuredSlide && (!hasNamedColorMatch || featuredMatchesColor);
      const preferredId = trustFeatured
        ? featuredSlide.dataset.mediaId
        : (hasNamedColorMatch ? namedColorSlide.dataset.mediaId : (featuredMedia?.id ? String(featuredMedia.id) : ''));
      if (preferredId && !matchingIds.includes(preferredId)) matchingIds.unshift(preferredId);
      const shouldFilter = color && matchingIds.length > 0;
      this.slides.forEach(slide => { slide.hidden = shouldFilter && !matchingIds.includes(String(slide.dataset.mediaId)); });
      this.thumbs.forEach(thumb => { thumb.hidden = shouldFilter && !matchingIds.includes(String(thumb.dataset.mediaId)); });
      const targetId = preferredId || matchingIds[0];
      if (targetId) this.goToMedia(targetId);
      else this.goTo(this.visibleIndexes()[0] || 0);
    }
  }
  customElements.define('product-gallery', ProductGallery);

  class VariantMediaGrid extends HTMLElement {
    connectedCallback() { this.items = $$('[data-featured-media-item]', this); }
    selectVariantMedia(featuredMedia, color, context) {
      if (!this.items?.length) return;
      let candidates = mediaCandidates(this.items, color, context);
      const featured = this.items.find(item => itemMatchesFeatured(item, featuredMedia));
      const namedCandidates = candidates.filter(item => mediaMatchesColor(item.dataset.mediaToken, color));
      const hasNamedColorMatch = namedCandidates.length > 0;
      const featuredMatchesColor = featured && mediaMatchesColor(featured.dataset.mediaToken, color);
      if (hasNamedColorMatch) {
        candidates = [...namedCandidates, ...candidates.filter(item => !namedCandidates.includes(item))];
      } else if (featured && (!hasNamedColorMatch || featuredMatchesColor)) {
        candidates = [featured, ...candidates.filter(item => item !== featured)];
      }
      if (!candidates.length) candidates = this.items.slice(0, 2);
      const shown = new Set(candidates.slice(0, 2));
      this.items.forEach(item => { item.hidden = !shown.has(item); });
    }
  }
  customElements.define('variant-media-grid', VariantMediaGrid);

  // Native Shopify facet URLs with section-only refreshes. The clean URL is
  // pushed to history, so combinations remain shareable and back/forward-safe.
  let collectionAbortController = null;
  async function refreshCollection(cleanUrl, pushState = true) {
    const current = $('[data-collection-section]');
    if (!current) { location.href = cleanUrl; return; }
    collectionAbortController?.abort();
    collectionAbortController = new AbortController();
    const requestUrl = new URL(cleanUrl, location.origin);
    requestUrl.searchParams.set('section_id', current.id.replace('CollectionSection-', ''));
    current.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(requestUrl, { signal: collectionAbortController.signal });
      if (!response.ok) throw new Error('No se pudieron actualizar los filtros.');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-collection-section]', doc);
      if (!fresh) throw new Error('Respuesta de colección incompleta.');
      current.replaceWith(fresh);
      document.documentElement.classList.remove('no-scroll');
      if (pushState) history.pushState({ lmCollection: true }, '', cleanUrl);
      window.LMannounce?.('Resultados actualizados');
    } catch (error) {
      if (error.name !== 'AbortError') location.href = cleanUrl;
    }
  }
  function urlFromForm(form) {
    const url = new URL(form.action || location.href, location.origin);
    url.search = new URLSearchParams(new FormData(form)).toString();
    return url.toString();
  }
  document.addEventListener('change', event => {
    const form = event.target.closest('[data-collection-section] [data-facets-form], [data-collection-section] [data-sort-form]');
    if (form) refreshCollection(urlFromForm(form));
  });
  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-collection-section] #mobile-facets');
    if (!form) return;
    event.preventDefault();
    refreshCollection(urlFromForm(form));
  });
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-collection-section] .active-facet, [data-collection-section] .active-facets__clear, [data-collection-section] .pagination a');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    refreshCollection(link.href);
  });
  window.addEventListener('popstate', () => {
    if ($('[data-collection-section]')) refreshCollection(location.href, false);
  });

  // Sticky ATC
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById('ProductForm');
      if (!this.form) return;
      this.priceEl = $('[data-variant-price]', this);
      this.syncOfferPrice = () => {
        const selectedOffer = $('.volume-offer--selected [data-offer-total]', this.form);
        if (this.priceEl && selectedOffer) this.priceEl.textContent = selectedOffer.textContent;
      };
      document.addEventListener('lm:offer:change', this.syncOfferPrice);
      document.addEventListener('lm:variant:change', this.syncOfferPrice);
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          this.dataset.visible = entry.isIntersecting ? 'false' : 'true';
        });
      }, { rootMargin: '0px 0px -60% 0px' });
      observer.observe(this.form);
      $('[data-sticky-atc-btn]', this)?.addEventListener('click', () => {
        this.form.requestSubmit?.() || this.form.querySelector('[type="submit"]')?.click();
      });
    }
    disconnectedCallback() {
      document.removeEventListener('lm:offer:change', this.syncOfferPrice);
      document.removeEventListener('lm:variant:change', this.syncOfferPrice);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  // Product forms use Shopify's real AJAX cart endpoint, then refresh and
  // open the configured cart drawer. Native form action remains the fallback.
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form || !window.fetch || !document.getElementById('CartDrawer')) return;
    e.preventDefault();
    const btn = $('[data-atc-btn]', form);
    const error = $('[data-product-form-error]', form);
    const original = btn?.textContent;
    const configurator = $('[data-volume-offers]', form);
    const bundleQuantity = configurator?.currentQuantity?.() || 1;
    if (error) { error.hidden = true; error.textContent = ''; }
    if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); btn.textContent = 'Añadiendo…'; }
    try {
      const protectionSelected = !!configurator?.protectionSelected?.();
      const customCartRequired = bundleQuantity > 1 || protectionSelected;
      const cartItems = customCartRequired ? configurator?.getCartItems?.() : null;
      if (customCartRequired && !cartItems) throw new Error('Revisa el color, la talla y la protección de envío antes de continuar.');
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: cartItems ? { 'Accept': 'application/json', 'Content-Type': 'application/json' } : { 'Accept': 'application/json' },
        body: cartItems ? JSON.stringify({ items: cartItems }) : new FormData(form),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.description || payload.message || 'No se pudo añadir el producto al carrito.');
      dispatch('lm:cart:added');
      window.LMannounce?.(document.documentElement.dataset.itemAddedText || 'Producto añadido al carrito');
    } catch (err) {
      if (error) { error.textContent = err.message; error.hidden = false; }
      else window.LMannounce?.(err.message);
    } finally {
      if (btn) { btn.disabled = !!configurator && !configurator.validateSelection?.(); btn.removeAttribute('aria-busy'); btn.textContent = original; }
    }
  });

  // Quick add form (grid)
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-quick-add-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('button');
    const original = btn?.textContent;
    if (btn) { btn.disabled = true; btn.textContent = '…'; }
    try {
      const fd = new FormData(form);
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: fd,
      });
      if (!res.ok) throw new Error();
      dispatch('lm:cart:added');
      window.LMannounce?.(document.documentElement.dataset.itemAddedText || 'Producto añadido al carrito');
    } catch (err) {
      console.warn(err);
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = original; }
    }
  });

  // ---------------------------------------------------------------------
  // Newsletter forms (popup / footer / embed) + back-in-stock requests.
  //
  // These submit as REAL, native browser POSTs to Shopify's own /contact
  // endpoint — deliberately NOT intercepted with fetch()/preventDefault.
  // Shopify binds hCaptcha to this exact endpoint from its own injected
  // script; a synthetic fetch() never lets that binding attach a token,
  // so the request comes back 403 (confirmed against the live preview
  // theme). Letting the native submit happen is what makes Shopify's own
  // bot protection work. What JS here does instead: read the server's
  // verdict (`form.posted_successfully?` / `form.errors`, rendered by
  // Liquid into a `[data-form-result]` marker) on the page that loads
  // after the redirect, and turn that into the nicer panel/opened-popup
  // UI. A client-side honeypot + time-trap still blocks obviously
  // scripted (non-hCaptcha-solving) submissions before they're sent.
  // ---------------------------------------------------------------------
  const SUBSCRIBED_KEY = 'lm:newsletter:subscribed';

  function isLikelyBot(form) {
    const honeypot = $('input[name="website"]', form);
    const tsInput = $('.newsletter-antibot__ts', form);
    const ts = parseInt(tsInput?.value || '0', 10) * 1000;
    const elapsed = ts ? Date.now() - ts : Infinity;
    return !!(honeypot && honeypot.value) || elapsed < 1500;
  }

  // Two-tier welcome offer: base code for anyone who registers, a better
  // code if they also opt into marketing (the consent checkbox). Both tiers
  // hit the same Shopify /contact endpoint — only the hidden form_type and
  // contact[tags] fields change, so unchecking the box never creates a
  // marketing-subscribed customer. The tier choice has to survive the full
  // page redirect Shopify does on submit, so it's stashed in sessionStorage
  // right before the native submit and read back once on the next load.
  const PENDING_TIER_KEY = 'lm:newsletter:pending-tier';
  const NEWSLETTER_FORM_SELECTOR = '.newsletter-popup__form, .footer-newsletter-form, .newsletter-form';

  document.addEventListener('submit', (e) => {
    const form = e.target.closest(`${NEWSLETTER_FORM_SELECTOR}, .restock-request__form`);
    if (!form) return;
    if (isLikelyBot(form)) {
      e.preventDefault(); // scripted/instant fill — drop silently, no native submit, no feedback
      return;
    }
    if (!form.matches(NEWSLETTER_FORM_SELECTOR)) return;
    const consent = $('.newsletter-consent__input', form);
    const tier = consent && consent.checked ? '15' : '10';
    try { sessionStorage.setItem(PENDING_TIER_KEY, tier); } catch {}
    if (tier === '10') {
      const formTypeInput = $('input[name="form_type"]', form);
      const tagsInput = $('input[name="contact[tags]"]', form);
      if (formTypeInput) formTypeInput.value = 'contact';
      if (tagsInput) tagsInput.disabled = true; // disabled fields aren't submitted
    }
  });

  // Reveal the server-rendered result of a just-submitted form: swap the
  // form panel for the success panel, or surface the error inline.
  function syncFormResult(form) {
    const resultEl = $('[data-form-result]', form);
    if (!resultEl) return null;
    const ok = resultEl.dataset.formResult === 'success';
    const wrapper = form.closest('[data-form-panel="form"]');
    const successPanel = wrapper?.parentElement ? $('[data-form-panel="success"]', wrapper.parentElement) : null;
    if (ok && wrapper && successPanel) {
      let tier = '10';
      try {
        tier = sessionStorage.getItem(PENDING_TIER_KEY) || '10';
        sessionStorage.removeItem(PENDING_TIER_KEY);
      } catch {}
      $$('[data-success-tier]', successPanel).forEach((el) => { el.hidden = el.dataset.successTier !== tier; });
      wrapper.hidden = true;
      successPanel.hidden = false;
      announce(successPanel.textContent.trim());
    } else if (!ok) {
      resultEl.hidden = false;
      announce(resultEl.textContent.trim());
    }
    return ok;
  }

  $$(NEWSLETTER_FORM_SELECTOR).forEach((form) => {
    if (syncFormResult(form)) {
      try { localStorage.setItem(SUBSCRIBED_KEY, '1'); } catch {}
    }
  });

  // ---------------------------------------------------------------------
  // Back-in-stock requests. Capture + duplicate-prevention only — this
  // is real and functional today. Actually emailing the shopper back
  // once stock returns still needs either a Shopify Flow workflow
  // (inventory-quantity trigger, built in the Flow UI — Flow has no
  // public API to author from files) or a dedicated restock app.
  // ---------------------------------------------------------------------
  function restockKey(variantId) { return `lm:restock:${variantId}`; }

  function refreshRestockBlockState(block) {
    const variantId = block.dataset.variantId;
    const requested = variantId && localStorage.getItem(restockKey(variantId));
    const formPanel = $('[data-restock-panel="form"]', block);
    const successPanel = $('[data-restock-panel="success"]', block);
    const alreadyPanel = $('[data-restock-panel="already"]', block);
    if (!formPanel || !successPanel || !alreadyPanel) return;
    formPanel.hidden = !!requested;
    successPanel.hidden = true;
    alreadyPanel.hidden = !requested;
  }

  $$('[data-restock-request]').forEach((block) => {
    const form = $('.restock-request__form', block);
    if (form && syncFormResult(form)) {
      try { localStorage.setItem(restockKey(block.dataset.variantId), '1'); } catch {}
    } else {
      refreshRestockBlockState(block);
    }
  });

  document.addEventListener('lm:variant:change', (e) => {
    const variant = e.detail?.variant;
    if (!variant) return;
    $$('[data-restock-request]').forEach((block) => {
      const isCurrent = block.dataset.variantId == String(variant.id);
      block.hidden = !(isCurrent && variant.available === false);
      if (isCurrent) refreshRestockBlockState(block);
    });
  });

  // Copy link buttons
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-copy-url]');
    if (!btn) return;
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(window.location.href);
      const orig = btn.textContent;
      btn.textContent = btn.dataset.copiedText || '✓';
      setTimeout(() => btn.textContent = orig, 1600);
    } catch {}
  });

  // Newsletter popup
  const POPUP_DISMISSED_UNTIL_KEY = 'lm:newsletter-popup:10-bolso:v1:dismissed-until';
  const POPUP_MIN_FLOOR_MS = 2000; // never trigger (even via exit-intent) before this
  const POPUP_SUPPRESS_DAYS = 14;

  class LmPopup extends HTMLElement {
    connectedCallback() {
      $$('[data-popup-close]', this).forEach(el => el.addEventListener('click', () => this.dismiss()));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') trapFocus(this, e);
      });

      // Just came back from a real /contact submit (see syncFormResult
      // above, which already swapped the inner panels) — show the
      // confirmation/error instead of waiting for the timer.
      if ($('[data-form-result]', this)) {
        this.show();
        return;
      }

      const dismissedUntil = parseInt(localStorage.getItem(POPUP_DISMISSED_UNTIL_KEY) || '0', 10);
      if (dismissedUntil && Date.now() < dismissedUntil) return;
      if (localStorage.getItem(SUBSCRIBED_KEY)) return;

      const delay = Math.max((parseFloat(this.dataset.delay) || 7) * 1000, POPUP_MIN_FLOOR_MS);
      this.timer = setTimeout(() => this.show(), delay);

      const isFinePointer = window.matchMedia('(pointer: fine)').matches;
      if (isFinePointer) {
        this._onExitIntent = (e) => {
          if (Date.now() - this._connectedAt < POPUP_MIN_FLOOR_MS || this.hasAttribute('open')) return;
          if (e.clientY <= 0) this.show();
        };
        document.addEventListener('mouseout', this._onExitIntent);
      } else {
        this._onScroll = () => {
          if (Date.now() - this._connectedAt < POPUP_MIN_FLOOR_MS || this.hasAttribute('open')) return;
          const max = document.documentElement.scrollHeight - window.innerHeight;
          if (max > 0 && window.scrollY / max >= 0.5) this.show();
        };
        window.addEventListener('scroll', this._onScroll, { passive: true });
      }
      this._connectedAt = Date.now();
    }
    show() {
      clearTimeout(this.timer);
      this._lastFocused = document.activeElement;
      this.setAttribute('open', '');
      document.body.classList.add('no-scroll');
      $('input, button', this)?.focus();
    }
    dismiss() {
      clearTimeout(this.timer);
      if (this._onExitIntent) document.removeEventListener('mouseout', this._onExitIntent);
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      this.removeAttribute('open');
      document.body.classList.remove('no-scroll');
      try {
        const until = Date.now() + POPUP_SUPPRESS_DAYS * 24 * 60 * 60 * 1000;
        localStorage.setItem(POPUP_DISMISSED_UNTIL_KEY, String(until));
      } catch {}
      this._lastFocused?.focus?.();
    }
  }
  customElements.define('lm-popup', LmPopup);

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const popup = $('lm-popup[open]');
    popup?.dismiss?.();
  });

  // ---------------------------------------------------------------------
  // Cart exit-intent nudge. Only arms itself once we've confirmed (via
  // /cart.js — the AJAX cart can change item_count without a page reload)
  // that there's actually something in the cart, and only once per
  // session either way, so it can never nag or trap someone repeatedly.
  // ---------------------------------------------------------------------
  class LmCartNudge extends HTMLElement {
    connectedCallback() {
      this.storageKey = 'lm:cart-nudge:shown';
      this._armed = false;
      $$('[data-cart-nudge-close]', this).forEach((el) => el.addEventListener('click', () => this.dismiss()));
      this.addEventListener('keydown', (e) => { if (e.key === 'Tab') trapFocus(this, e); });

      // Cart state can change without a page load (quick-add from a
      // collection grid, drawer). Check now, and again whenever something
      // is actually added, instead of only once at page load.
      this.armExitAndBackTriggers();
      document.addEventListener('lm:cart:added', () => this.armExitAndBackTriggers());

      // Closing the cart drawer with items still in it is the clearest
      // signal of hesitation there is — show immediately, no grace period,
      // reusing the drawer's own real close event (lm-drawer dispatches
      // lm:drawer:close from base.js's generic DrawerElement).
      document.addEventListener('lm:drawer:close', (e) => {
        if (e.detail?.drawer?.id === 'CartDrawer') this.showOnCartDrawerClose();
      });
    }
    canShow() {
      return !this.hasAttribute('open') && !$('lm-popup[open]') && !sessionStorage.getItem(this.storageKey);
    }
    async cartHasItems() {
      try {
        const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
        return !!cart && cart.item_count > 0;
      } catch { return false; }
    }
    async showOnCartDrawerClose() {
      if (this.hasAttribute('open') || $('lm-popup[open]')) return;
      if (Date.now() - (this._lastCartCloseNudge || 0) < 60000) return;
      if (await this.cartHasItems()) {
        this._lastCartCloseNudge = Date.now();
        // A deliberate cart close is stronger than generic exit intent. Show
        // once per minute even if the session-level exit nudge was seen before.
        this.show();
      }
    }
    async armExitAndBackTriggers() {
      if (this._armed || sessionStorage.getItem(this.storageKey)) return;
      if (!(await this.cartHasItems())) return;
      this._armed = true;

      const armedAt = Date.now();
      const canShow = () => this.canShow() && Date.now() - armedAt > 800;

      if (window.matchMedia('(pointer: fine)').matches) {
        this._onExitIntent = (e) => { if (canShow() && e.clientY <= 0) this.show(); };
        document.addEventListener('mouseout', this._onExitIntent);
      }

      // Back-button trap: push one extra history entry so the first back
      // press can be caught and turned into the nudge instead of leaving.
      // Fires at most once — after it fires (or once dismissed) normal
      // back navigation is restored, so nobody gets stuck.
      history.pushState({ lmCartNudge: true }, '', location.href);
      this._onPopState = () => {
        if (canShow()) {
          history.pushState({ lmCartNudge: true }, '', location.href);
          this.show();
        }
      };
      window.addEventListener('popstate', this._onPopState);
    }
    show() {
      this._lastFocused = document.activeElement;
      this.setAttribute('open', '');
      document.body.classList.add('no-scroll');
      $('.cart-nudge__close', this)?.focus();
      try { sessionStorage.setItem(this.storageKey, '1'); } catch {}
    }
    dismiss() {
      this.removeAttribute('open');
      document.body.classList.remove('no-scroll');
      if (this._onExitIntent) document.removeEventListener('mouseout', this._onExitIntent);
      if (this._onPopState) window.removeEventListener('popstate', this._onPopState);
      try { sessionStorage.setItem(this.storageKey, '1'); } catch {}
      this._lastFocused?.focus?.();
    }
  }
  customElements.define('lm-cart-nudge', LmCartNudge);

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const nudge = $('lm-cart-nudge[open]');
    nudge?.dismiss?.();
  });

  // Spain-local commercial dates. The text fallback remains accurate without JS.
  const updateCommercialDates = () => {
    const offerEls = $$('[data-offer-date]');
    if (!offerEls.length) return;

    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(new Date()).reduce((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = Number(part.value);
      return acc;
    }, {});
    const shopToday = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
    const shortDate = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(shopToday).replace('.', '');

    offerEls.forEach(el => { el.textContent = `· ${shortDate}`; });
  };
  updateCommercialDates();

  class AnnouncementCountdown extends HTMLElement {
    connectedCallback() {
      const endAt = Date.parse(this.dataset.endAt || '');
      const timeEl = $('[data-announcement-countdown-time]', this);
      const copyEl = $('[data-announcement-countdown-copy]', this);
      if (!Number.isFinite(endAt) || !timeEl) {
        this.hidden = true;
        return;
      }
      const tick = () => {
        const remaining = endAt - Date.now();
        if (remaining <= 0) {
          clearInterval(this._timer);
          if (this.dataset.expiredBehavior === 'message' && this.dataset.expiredText) {
            if (copyEl) copyEl.textContent = this.dataset.expiredText;
            timeEl.textContent = '';
          } else {
            this.hidden = true;
          }
          return;
        }
        const totalSeconds = Math.floor(remaining / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        timeEl.textContent = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      };
      tick();
      this._timer = setInterval(tick, 1000);
    }
    disconnectedCallback() {
      clearInterval(this._timer);
    }
  }
  if (!customElements.get('announcement-countdown')) customElements.define('announcement-countdown', AnnouncementCountdown);

  // Review apps can insert their target asynchronously; resolve it at click time.
  $$('[data-review-summary]').forEach(link => {
    link.addEventListener('click', event => {
      const target = document.querySelector('#product-reviews');
      if (!target) event.preventDefault();
    });
  });

  // Scroll reveal
  const revealSelector = '.section-header, .editorial-banner__content, .editorial-banner__media, .benefit, .product-card, .collection-card, .hero__content, .featured-product > *, .color-editorial__hero, .color-story__header';
  const revealTargets = $$(revealSelector).filter(el => !el.closest('[data-cart-drawer-content], .drawer'));
  if (revealTargets.length && 'IntersectionObserver' in window) {
    revealTargets.forEach(el => el.classList.add('reveal'));
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal--visible');
          entry.target.closest('.color-story')?.classList.add('color-story--visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    revealTargets.forEach(el => revealObserver.observe(el));
  }

})();

// Product-card to PDP zoom transition. Navigation remains native if animation is unavailable.
(() => {
  if (window.__lmPdpZoomTransitionReady) return;
  window.__lmPdpZoomTransitionReady = true;
    window.addEventListener('pageshow', () => {
        document.querySelectorAll('.lm-pdp-zoom-clone').forEach((el) => el.remove());
            document.documentElement.classList.remove('lm-pdp-zooming');
              });
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.product-card a[href*="/products/"]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const card = link.closest('.product-card');
    const image = card?.querySelector('.product-card__media img, .product-card__image');
    if (!image || !image.getBoundingClientRect) return;
    const rect = image.getBoundingClientRect();
    if (rect.width < 20 || rect.height < 20) return;
    event.preventDefault();
    const clone = image.cloneNode(true);
    clone.className = 'lm-pdp-zoom-clone';
    Object.assign(clone.style, { left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px`, borderRadius: getComputedStyle(card?.querySelector('.product-card__media') || image).borderRadius || '0px' });
    document.body.appendChild(clone);
    document.documentElement.classList.add('lm-pdp-zooming');
    const scale = Math.max(window.innerWidth / rect.width, window.innerHeight / rect.height) * 1.03;
    const dx = window.innerWidth / 2 - (rect.left + rect.width / 2);
    const dy = window.innerHeight / 2 - (rect.top + rect.height / 2);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      clone.style.transform = `translate(${dx}px, ${dy}px) scale(${scale})`;
      clone.style.opacity = '.18';
      clone.style.borderRadius = '0px';
    }));
    setTimeout(() => { window.location.href = link.href; }, 310);
  }, true);
})();

/* Alba Duarte — input intent + card galleries + press interaction.
 * Kept isolated from the core bundle so Theme Editor section reloads can
 * re-initialize safely without duplicate listeners. */
(() => {
  'use strict';

  const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initTouchIntentCarousels(root = document) {
    // Use the browser's native nested scrolling, exactly like the review
    // carousels: vertical intent stays with the document and horizontal
    // intent moves the overflow-x track. No touchmove preventDefault here.
    all('[data-carousel-track]', root).forEach((track) => {
      if (track.matches('.lm-review-strip__track, .lm-ugc-track, [data-review-gallery-track]')) return;
      track.dataset.touchIntentReady = 'native';
    });
  }

  function initProductCardGalleries(root = document) {
    all('.product-card[data-card-gallery-initial]', root).forEach((card) => {
      if (card.dataset.galleryReady === 'true') return;
      const payload = card.querySelector('[data-card-gallery-data]');
      const previous = card.querySelector('[data-card-gallery-prev]');
      const next = card.querySelector('[data-card-gallery-next]');
      const primary = card.querySelector('.product-card__media img:first-of-type');
      const secondary = card.querySelector('.product-card__media img:nth-of-type(2)');
      if (!payload || !previous || !next || !primary || !secondary) return;

      let images;
      try { images = JSON.parse(payload.textContent || '[]'); } catch { return; }
      if (!Array.isArray(images) || images.length < 2) return;

      card.dataset.galleryReady = 'true';
      const initialId = String(card.dataset.cardGalleryInitial || '');
      let current = Math.max(0, images.findIndex((image) => String(image.id) === initialId));
      let switching = false;
      let pinTimer = null;

      const applySource = (img, item) => {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = item.src;
        img.alt = item.alt || card.dataset.productTitle || '';
      };

      const primeSecondary = () => {
        const item = images[(current + 1) % images.length];
        applySource(secondary, item);
      };

      const show = (index) => {
        if (switching) return;
        const targetIndex = (index + images.length) % images.length;
        if (targetIndex === current) return;
        const item = images[targetIndex];
        switching = true;
        window.clearTimeout(pinTimer);
        card.classList.add('is-gallery-pinned');

        const preload = new Image();
        preload.decoding = 'async';
        preload.src = item.src;
        const commit = () => {
          applySource(secondary, item);
          card.classList.add('is-gallery-switching');
          window.setTimeout(() => {
            applySource(primary, item);
            current = targetIndex;
            card.classList.remove('is-gallery-switching');
            // Once the visible layer is back on the primary image, quietly
            // prime the next hover frame while it is fully transparent.
            window.requestAnimationFrame(primeSecondary);
            switching = false;
            pinTimer = window.setTimeout(() => card.classList.remove('is-gallery-pinned'), 780);
          }, reduceMotion() ? 0 : 190);
        };
        if (preload.complete) commit();
        else {
          preload.addEventListener('load', commit, { once: true });
          preload.addEventListener('error', () => { switching = false; card.classList.remove('is-gallery-pinned'); }, { once: true });
        }
      };

      const onArrow = (direction) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        show(current + direction);
      };
      previous.addEventListener('click', onArrow(-1));
      next.addEventListener('click', onArrow(1));
      previous.addEventListener('pointerdown', (event) => event.stopPropagation());
      next.addEventListener('pointerdown', (event) => event.stopPropagation());
    });
  }

  function initPressTouch(root = document) {
    all('.lm-press__item', root).forEach((item) => {
      if (item.dataset.pressTouchReady === 'true') return;
      item.dataset.pressTouchReady = 'true';
      let timer = null;
      const release = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => item.classList.remove('is-touch-active'), 160);
      };
      item.addEventListener('touchstart', () => {
        all('.lm-press__item.is-touch-active').forEach((other) => {
          if (other !== item) other.classList.remove('is-touch-active');
        });
        window.clearTimeout(timer);
        item.classList.add('is-touch-active');
      }, { passive: true });
      item.addEventListener('touchend', release, { passive: true });
      item.addEventListener('touchcancel', release, { passive: true });
    });
  }

  function initCollectionTouch(root = document) {
    all('.aura-collections__card', root).forEach((card) => {
      if (card.dataset.collectionTouchReady === 'true') return;
      card.dataset.collectionTouchReady = 'true';
      let timer = null;
      const release = () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => card.classList.remove('is-touch-active'), 160);
      };
      card.addEventListener('touchstart', () => {
        all('.aura-collections__card.is-touch-active').forEach((other) => {
          if (other !== card) other.classList.remove('is-touch-active');
        });
        window.clearTimeout(timer);
        card.classList.add('is-touch-active');
      }, { passive: true });
      card.addEventListener('touchend', release, { passive: true });
      card.addEventListener('touchcancel', release, { passive: true });
    });
  }

  function initStockRotators(root = document) {
    all('[data-stock-rotator]', root).forEach((rotator) => {
      if (rotator.dataset.rotatorReady === 'true') return;
      const messages = all('.lm-product__stock-message', rotator);
      if (messages.length < 2) return;
      rotator.dataset.rotatorReady = 'true';
      let index = 0;
      const interval = Number(rotator.dataset.interval || 2800);
      window.setInterval(() => {
        messages[index].classList.remove('is-active');
        index = (index + 1) % messages.length;
        messages[index].classList.add('is-active');
      }, interval);
    });
  }

  function initReviewCarousels(root = document) {
    all('[data-review-carousel]', root).forEach((carousel) => {
      if (carousel.dataset.reviewReady === 'true') return;
      const slides = all('.lm-product-review-card__slide', carousel);
      const previous = carousel.querySelector('[data-review-prev]');
      const next = carousel.querySelector('[data-review-next]');
      if (!slides.length || !previous || !next) return;
      carousel.dataset.reviewReady = 'true';
      let index = 0;
      let timer = null;
      const interval = Math.max(3000, Number(carousel.dataset.reviewAutoplay || 4200));
      const show = (newIndex) => {
        slides[index].classList.remove('is-active');
        index = (newIndex + slides.length) % slides.length;
        slides[index].classList.add('is-active');
      };
      const start = () => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || slides.length < 2) return;
        window.clearInterval(timer);
        timer = window.setInterval(() => show(index + 1), interval);
      };
      const pauseThenResume = () => {
        window.clearInterval(timer);
        window.setTimeout(start, 4500);
      };
      previous.addEventListener('click', () => { show(index - 1); pauseThenResume(); });
      next.addEventListener('click', () => { show(index + 1); pauseThenResume(); });
      carousel.addEventListener('pointerdown', pauseThenResume, { passive: true });
      start();
    });
  }

  function initStoryAutoplay(root = document) {
    all('[data-story-carousel]', root).forEach((carousel) => {
      if (carousel.dataset.storyReady === 'true') return;
      const cards = all('.lm-product-story__card', carousel);
      if (cards.length < 2) return;
      carousel.dataset.storyReady = 'true';
      let timer = null;
      let index = 0;
      const interval = Math.max(3200, Number(carousel.dataset.storyAutoplay || 4200));
      const go = (nextIndex) => {
        index = (nextIndex + cards.length) % cards.length;
        carousel.scrollTo({ left: cards[index].offsetLeft - carousel.offsetLeft, behavior: 'smooth' });
      };
      const start = () => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        window.clearInterval(timer);
        timer = window.setInterval(() => go(index + 1), interval);
      };
      const pauseThenResume = () => {
        window.clearInterval(timer);
        window.setTimeout(start, 5200);
      };
      carousel.addEventListener('pointerdown', pauseThenResume, { passive: true });
      carousel.addEventListener('touchstart', pauseThenResume, { passive: true });
      carousel.addEventListener('scroll', () => {
        const left = carousel.scrollLeft;
        let best = 0;
        let dist = Infinity;
        cards.forEach((card, i) => {
          const d = Math.abs(card.offsetLeft - carousel.offsetLeft - left);
          if (d < dist) { dist = d; best = i; }
        });
        index = best;
      }, { passive: true });
      start();
    });
  }

  const init = (root = document) => {
    initTouchIntentCarousels(root);
    initProductCardGalleries(root);
    initPressTouch(root);
    initCollectionTouch(root);
    initStockRotators(root);
    initReviewCarousels(root);
    initStoryAutoplay(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target || document));
})();


/* Round 15: robust product-card arrows + touch preview on first tap. */
(() => {
  const GALLERY_SELECTOR = '[data-card-gallery-prev], [data-card-gallery-next]';
  const PREVIEW_SELECTOR = '.product-card--has-hover .product-card__media';
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isCoarsePointer = () => window.matchMedia('(hover: none), (pointer: coarse)').matches;

  const parseGallery = (card) => {
    if (!card) return [];
    if (Array.isArray(card.__lmGallery)) return card.__lmGallery;
    const payload = card.querySelector('[data-card-gallery-data]');
    if (!payload) return [];
    try {
      const images = JSON.parse(payload.textContent || '[]');
      card.__lmGallery = Array.isArray(images) ? images : [];
      return card.__lmGallery;
    } catch (_) {
      card.__lmGallery = [];
      return card.__lmGallery;
    }
  };

  const applyImage = (img, item, fallbackAlt = '') => {
    if (!img || !item?.src) return;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = item.src;
    img.alt = item.alt || fallbackAlt;
  };

  const currentIndex = (card, images) => {
    const stored = Number(card?.dataset.galleryCaptureIndex);
    if (Number.isFinite(stored)) return Math.max(0, Math.min(images.length - 1, stored));
    const initialId = String(card?.dataset.cardGalleryInitial || '');
    const idx = images.findIndex((image) => String(image.id) === initialId);
    return idx >= 0 ? idx : 0;
  };

  const primeSecondary = (card, images, index) => {
    const secondary = card.querySelector('.product-card__media img:nth-of-type(2)');
    if (!secondary || images.length < 2) return;
    applyImage(secondary, images[(index + 1) % images.length], card.dataset.productTitle || '');
  };

  const setPreview = (card, active) => {
    if (!card || card.classList.contains('is-gallery-switching')) return;
    card.classList.toggle('is-showing-alt', !!active);
  };

  const clearPreview = (card, delay = 0) => {
    if (!card) return;
    window.clearTimeout(card.__lmPreviewTimer);
    card.__lmPreviewTimer = window.setTimeout(() => {
      card.classList.remove('is-showing-alt');
      delete card.dataset.touchPreviewArmed;
    }, delay);
  };

  const changeCardImage = (card, direction) => {
    if (!card || card.dataset.galleryCaptureBusy === 'true') return;
    const images = parseGallery(card);
    if (images.length < 2) return;
    const primary = card.querySelector('.product-card__media img:first-of-type');
    const secondary = card.querySelector('.product-card__media img:nth-of-type(2)');
    if (!primary || !secondary) return;

    const index = currentIndex(card, images);
    const target = (index + direction + images.length) % images.length;
    const targetItem = images[target];
    const nextItem = images[(target + 1) % images.length];

    card.dataset.galleryCaptureBusy = 'true';
    card.dataset.galleryCaptureIndex = String(target);
    card.dataset.cardGalleryInitial = String(targetItem.id || '');
    card.classList.remove('is-showing-alt');

    const commit = () => {
      applyImage(secondary, targetItem, card.dataset.productTitle || '');
      card.classList.add('is-gallery-switching', 'is-gallery-pinned');
      window.setTimeout(() => {
        applyImage(primary, targetItem, card.dataset.productTitle || '');
        applyImage(secondary, nextItem, card.dataset.productTitle || '');
        card.classList.remove('is-gallery-switching');
        window.setTimeout(() => card.classList.remove('is-gallery-pinned'), 600);
        card.dataset.galleryCaptureBusy = 'false';
      }, reduceMotion() ? 0 : 150);
    };

    const preload = new Image();
    preload.decoding = 'async';
    preload.src = targetItem.src;
    if (preload.complete) commit();
    else {
      preload.addEventListener('load', commit, { once: true });
      preload.addEventListener('error', () => {
        commit();
      }, { once: true });
    }
  };

  const triggerArrow = (button) => {
    const card = button?.closest('.product-card');
    if (!card) return;
    const now = Date.now();
    const last = Number(card.dataset.galleryActionAt || '0');
    if (now - last < 260) return;
    card.dataset.galleryActionAt = String(now);
    changeCardImage(card, button.hasAttribute('data-card-gallery-prev') ? -1 : 1);
  };

  document.addEventListener('pointerdown', (event) => {
    const button = event.target.closest(GALLERY_SELECTOR);
    if (button) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      triggerArrow(button);
      return;
    }

    if (!isCoarsePointer()) return;
    const media = event.target.closest(PREVIEW_SELECTOR);
    if (!media) return;
    const card = media.closest('.product-card');
    if (!card || !card.querySelector('.product-card__media img:nth-of-type(2)')) return;
    card.dataset.touchPreviewArmed = 'true';
    setPreview(card, true);
  }, true);

  document.addEventListener('pointerup', (event) => {
    const media = event.target.closest?.(PREVIEW_SELECTOR);
    const card = media?.closest('.product-card');
    if (card) clearPreview(card, 180);
  }, true);

  document.addEventListener('pointercancel', (event) => {
    const media = event.target.closest?.(PREVIEW_SELECTOR);
    const card = media?.closest('.product-card');
    if (card) clearPreview(card, 0);
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest(GALLERY_SELECTOR);
    if (button) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    if (!isCoarsePointer()) return;
    const media = event.target.closest(PREVIEW_SELECTOR);
    if (!media) return;
    const card = media.closest('.product-card');
    if (!card || card.dataset.touchPreviewArmed !== 'true') return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    clearPreview(card, 180);
  }, true);

  const primeVisibleCards = (root = document) => {
    root.querySelectorAll('.product-card').forEach((card) => {
      const secondary = card.querySelector('.product-card__media img:nth-of-type(2)');
      if (!secondary) return;
      const src = secondary.currentSrc || secondary.src;
      if (!src) return;
      const preload = new Image();
      preload.decoding = 'async';
      preload.src = src;
      const images = parseGallery(card);
      if (images.length > 1) primeSecondary(card, images, currentIndex(card, images));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => primeVisibleCards(), { once: true });
  } else {
    primeVisibleCards();
  }
  document.addEventListener('shopify:section:load', (event) => primeVisibleCards(event.target || document));
})();

